import { EYEBROW_BASE } from "../../lib/ui.js";
import { RELEASE_STATE, releaseEndpointConfirmed, releaseEndpointExcluded } from "../../lib/managedConfigClient.js";

export function plural(count, noun, pluralNoun = `${noun}s`) {
    return `${count.toLocaleString()} ${count === 1 ? noun : pluralNoun}`;
}

/// Confirmation counts for a release's pilot endpoints.
export function pilotProgress(release) {
    const endpoints = release?.endpoints ?? [];
    const excluded = endpoints.filter(releaseEndpointExcluded).length;
    const confirmed = endpoints.filter((endpoint) => !releaseEndpointExcluded(endpoint) && releaseEndpointConfirmed(endpoint)).length;
    return { total: endpoints.length, confirmed, excluded, unresolved: endpoints.length - confirmed - excluded };
}

/// The current step, from the open release. Step 2 has no server state: it is
/// the page in front of "Start pilot".
export function currentReleaseStep({ release, choosing = false }) {
    if (release?.state === RELEASE_STATE.PILOTING) {
        const { total, unresolved } = pilotProgress(release);
        return total > 0 && unresolved === 0 ? 4 : 3;
    }
    return choosing ? 2 : 1;
}

function pilotResult({ confirmed, excluded }) {
    return excluded ? `${confirmed.toLocaleString()} confirmed, ${excluded.toLocaleString()} excluded` : `${confirmed.toLocaleString()} confirmed`;
}

/// Each step's state and one line of detail. `draftChanges` counts the draft
/// against the current generation; while a release is open it counts the pilot
/// generation against the generation it replaces.
export function releaseSteps({ release, choosing = false, draftChanges = 0, selectedCount = 0, targetCount = null }) {
    const current = currentReleaseStep({ release, choosing });
    const progress = pilotProgress(release);
    const state = (number) => (number < current ? "done" : number === current ? "current" : "todo");
    const details = {
        1: draftChanges ? `${plural(draftChanges, "setting")} changed` : "",
        2: current === 2 ? `${selectedCount.toLocaleString()} selected` : current > 2 ? plural(progress.total, "endpoint") : "",
        3: current === 3
            ? `${progress.confirmed.toLocaleString()} of ${(progress.total - progress.excluded).toLocaleString()} confirmed`
            : current > 3 ? pilotResult(progress) : "",
        4: current === 4 && targetCount !== null ? plural(targetCount, "endpoint") : "",
    };
    return [
        { number: 1, label: "Edit draft" },
        { number: 2, label: "Choose pilot endpoints" },
        { number: 3, label: "Verify pilot endpoints" },
        { number: 4, label: "Sign and release to tenant" },
    ].map((step) => ({ ...step, state: state(step.number), detail: details[step.number] }));
}

const STATE_SUFFIX = { done: " done", current: " current", todo: "" };

/// The 6A and 6B step strip. `onSelect` makes the listed step numbers into
/// buttons, for moving between the read-only draft and an open release.
export default function StepStrip({ steps, selectable = [], onSelect, viewing }) {
    return (
        <ol className="m-0 grid list-none gap-[8px] p-0 md:grid-cols-4" aria-label="Release steps">
            {steps.map((step) => {
                const body = (
                    <>
                        <span className={`${EYEBROW_BASE} text-[10px] ${step.state === "current" ? "text-[color:var(--ink)]" : step.state === "done" ? "text-[color:var(--green)]" : "text-[color:var(--eyebrow)]"}`}>{`Step ${step.number}${STATE_SUFFIX[step.state]}`}</span>
                        <span className={`text-[12px] ${step.state === "todo" ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{step.label}</span>
                        {step.detail ? <span className="text-[11px] text-[color:var(--muted)]">{step.detail}</span> : null}
                    </>
                );
                const frame = [
                    "grid min-h-[58px] content-start gap-[1px] border px-[12px] py-[8px] text-left",
                    step.state === "current" ? "border-[color:var(--green)] bg-[color:var(--green-soft)]" : "border-[color:var(--line)] bg-[color:var(--bg)]",
                ].join(" ");
                return (
                    <li key={step.number} aria-current={step.state === "current" ? "step" : undefined} className="grid">
                        {selectable.includes(step.number) && onSelect ? (
                            <button type="button" className={`${frame} ${viewing === step.number ? "outline outline-1 outline-[color:var(--line-strong)]" : ""}`} aria-pressed={viewing === step.number} onClick={() => onSelect(step.number)}>{body}</button>
                        ) : <div className={frame}>{body}</div>}
                    </li>
                );
            })}
        </ol>
    );
}
